<template>
	<div class="col-12">
		<div class="row mt-4">
			<div class="col-1 p-0 text-center">
				<div class="position-relative h-100" id="icone-status-atual">
					<i class="fas fa-circle fa-lg text-warning"></i>
				</div>
			</div>
			<div class="col-11">
				<StatusAtual 
					:idServicoAtendimento="idServicoAtendimento" 
					:servico="servico" 
					:horario="horaStatus"
					:status="status" />
			</div>
			<div class="col-12 text-end mt-2">
				<BotaoVerMais
					:id="idBotaoVerMais"
					:classes="'btn-sm float-end'"
					:target="'historico'"
					:textoAbrir="'Ver mais'"
					:textoFechar="'Ver menos'"
					@acaoVerMais="abrirMaisDados" />
			</div>
		</div>

		<div class="row mt-1">
			<div class="col-12">
				<div id="historico" style="display: none;">
					<div class="row mapa" v-if="exibirMapa" :style="{ 'height': alturaMapa }">
						<MapaEnderecoAtendimento 
							ref="mapaEnderecoRef"
							:coordenadas="coordenadas"
							:idServicoAtendimento="idServicoAtendimento" />
					</div>
					<b-card class="border-light borda-redonda sombra" :style="estiloCardMaisDados">
						<MaisDados 
							:enderecoOrigem="enderecoOrigem" 
							:enderecoDestino="enderecoDestino" 
							:status="status"
							:servico="servico" 
							:exibirRastreioPrestadores="exibirRastreioPrestadores" />
					</b-card>
				</div>
			</div>
		</div>

		<div class="row mt-4">
			<div class="col-12" v-if="exibirCaixaMarcarChegada">
                <MarcarChegada :idServicoAtendimento="idServicoAtendimento" />
            </div>
		</div>

		<div class="row mt-4">
			<div class="col-12">
				<EnviarAnexos />
			</div>
		</div>
	</div>

</template>

<script setup>
import StatusAtual from '@/components/StatusAtual.vue';
import MapaEnderecoAtendimento from '@/components/Mapa/MapaEnderecoAtendimento.vue';
import BotaoVerMais from '@/components/Botoes/BotaoVerMais';
import MaisDados from '@/components/MaisDados.vue';
import MarcarChegada from '@/components/MarcarChegada.vue';
import EnviarAnexos from '@/components/EnviarAnexos.vue';
import { defineProps, ref, computed, nextTick, watch, onMounted } from 'vue';
import { isEmpty, uniqueID, slideDown, slideUp, tempoEmMinutos } from '@/helper/funcoes';
import * as CoordenadasAtendimentoService from '@/services/coordenadasAtendimento';
import { storeToRefs } from 'pinia';
import { useAtendimentoStore } from '@/stores/atendimento';
import moment from 'moment';

const storeAtendimento = useAtendimentoStore();
const { totalServicos, coordenadasAtendimento } = storeToRefs(storeAtendimento);
let estiloCardMaisDados = ref({});

const props = defineProps({
	exibirRastreioPrestadores: String,
	status: String,
	horaStatus: String,
	enderecoOrigem: Object,
	enderecoDestino: Object,
	idServicoAtendimento: String
});

const podeMarcarChegada = ref(false);
const idBotaoVerMais = 'ver-mais-dados-' + uniqueID();
const mostrarMais = ref(false);
const intervaloPodeMarcarChegada = ref(null);
const mapaEnderecoRef = ref(null);

const servico = computed(() => storeAtendimento.servico(props.idServicoAtendimento))

const exibirMapa = computed(() => {		
	const exibir = !isEmpty(servico.value) && 
		[
			'PRESTADOR_ACIONADO',
			'PRESTADOR_A_CAMINHO',
			'PRESTADOR_CHEGOU_LOCAL',
			'VIAJANDO_PARA_DESTINO',
			'FINALIZADO'
		].includes(props.status);
	alterarPosicaoCardMaisDados(exibir);
	return exibir;
});

const coordenadas = computed(() => {
	let coordenadas = [...coordenadasAtendimento.value];
	
	if (props.exibirRastreioPrestadores == 'S') {
		coordenadas.push(...CoordenadasAtendimentoService.montarCoordernasPrestador(servico.value));
	}
	return coordenadas;
});

const exibirCaixaMarcarChegada = computed(() => servico.value && servico.value.chegadaConfirmadaPor != 'SOLICITANTE' && podeMarcarChegada.value);
const alturaMapa = computed(() => props.exibirRastreioPrestadores == 'S' ? '70vh' : '40vh');

function alterarPosicaoCardMaisDados(exibindoMapa) {
	estiloCardMaisDados = {};
	if (exibindoMapa) {
		estiloCardMaisDados = {
			'margin-top': '-90px',
			'z-index': '1000',
			'position': 'relative'
		}
	}
} 

watch(() => servico.value, () => {	
	clicarVerMais();
    atualizarPodeMarcarChegada();
});

onMounted(() => {
	verificaPodeMarcarChegada();
    atualizarPodeMarcarChegada();
});

function abrirMaisDados() {
	mostrarMais.value = !mostrarMais.value;
	const elementoHistorico = document.getElementById("historico");

	if (mostrarMais.value) {
		nextTick(() => {
			slideDown(elementoHistorico, 300);
			setTimeout(() => {
				mapaEnderecoRef.value?.invalidateSize();
				mapaEnderecoRef.value?.centralizarMapa();
			}, 100);
		});
	} else {
		slideUp(elementoHistorico, 300);
	}
}

function atualizarPodeMarcarChegada() {
	limparIntervaloPodeMarcarChegada();
	podeMarcarChegada.value = false;
	if (totalServicos.value > 0) {
		intervaloPodeMarcarChegada.value = setInterval(() => {
			verificaPodeMarcarChegada();
		}, 2000);
	}
}

function verificaPodeMarcarChegada() {
	podeMarcarChegada.value = false;
	if (totalServicos.value > 0) {
		if (
			props.status == 'PRESTADOR_A_CAMINHO' && 
			!isEmpty(servico.value.previaServico) && !isEmpty(servico.value.dataCadastroServico)
		) {
			const previsaoMinutos = tempoEmMinutos(servico.value.previaServico);
			const dataCadastroServico = moment(servico.value.dataCadastroServico, "DD/MM/YYYY HH:mm:ss");
			const agora = moment();
			
			// Calcula a diferença em minutos entre a data em que o serviço foi cadastrado e a data de agora
			const duration = moment.duration(agora.diff(dataCadastroServico));
			const difMinutes = duration.asMinutes();
			
			// se a diferença for menor que 20% não deixa marcar a chegada
			const porcentagem = ((difMinutes * 100) / previsaoMinutos).toFixed(2);			
			if (porcentagem >= 20) {
				limparIntervaloPodeMarcarChegada();
				podeMarcarChegada.value = true;
			}
		} else if (!isEmpty(servico.value.dataHorarioChegada)) {		
			podeMarcarChegada.value = false;
		}
	}
}

function limparIntervaloPodeMarcarChegada() {
	if (intervaloPodeMarcarChegada.value) {
		clearInterval(intervaloPodeMarcarChegada.value);
		intervaloPodeMarcarChegada.value = null;
	}
}

function clicarVerMais() {
	setTimeout(() => {
		const botao = document.getElementById(idBotaoVerMais);
		const visivel = document.getElementById("historico").checkVisibility();		
		if (totalServicos.value > 0) {
			if (!visivel) {
				botao.click();
			}
		} else if (visivel) {
			botao.click();
		}
	}, 100);
}

</script>
